import { Breadcrumbs } from "components/breadcrumbs";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DetailEwaiverType, useDetailEwaiver } from "../hooks/useDetailEwaiver";
import { FullScreenLoading } from "components/fullScreenLoading";
import { CollapsibleSection } from "components/collapsibleSection";
import { SwitchButton } from "components/switchButton";
import dayjs from "dayjs";
import Button from "components/button";
import { ClockIcon, PaperClipIcon, PencilIcon } from "@heroicons/react/outline";
import Input from "components/input";
import useWindowWidth from "utills/InitWidthDevice";
import { generateUUID, isHaveActionPermission } from "utills/helpers";
import RadioButton from "components/radioButton";

const EwaiverDetailPage = () => {
  const params = useParams();
  const windowWidth = useWindowWidth();

  const id = params?.id ?? "";

  const { data, loading } = useDetailEwaiver(id);

  const [isMobile, setIsMobile] = useState(false);

  const renderEventName = (item: DetailEwaiverType | null) => {
    if (item === null) return "";

    if (item?.event_name_vi !== "") return item?.event_name_vi;
    if (item?.event_name !== "") return item?.event_name;

    return "";
  };

  const getStatusValue = (isHaveOnlineCheckin: boolean | number | string) => {
    // Number case
    if (isHaveOnlineCheckin === 1) return true;
    if (isHaveOnlineCheckin === 0) return false;

    // String case
    if (isHaveOnlineCheckin === "1") return true;
    if (isHaveOnlineCheckin === "0") return false;

    // Boolean case
    if (isHaveOnlineCheckin === true) return true;
    if (isHaveOnlineCheckin === false) return false;

    return true;
  };

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <div className="wrapper">
      {loading && <FullScreenLoading />}
      <Breadcrumbs
        customPageLabel="eWaiver Detail"
        additionalLabel={
          <div className="flex items-center gap-[8px]">
            {/* <Button
              className="w-auto py-[10px] bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
              onClick={(e: any) => {}}
            >
              <span className="sr-only">History Log</span>
              <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">Log</span>
            </Button> */}
            {isHaveActionPermission("", "e_waiver", "edit") ? (
              <Link to={`/ewaiver/edit/${id}`}>
                <Button className="w-auto py-[10px]">
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />

      <CollapsibleSection label="eWaiver Information" defaultOpen={true}>
        <div className="p-6 flex flex-col gap-[30px]">
          <section
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-4"
            }`}
          >
            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                Status
              </span>
              <SwitchButton
                disabled
                checked={
                  data?.is_have_online_checkin
                    ? getStatusValue(data?.is_have_online_checkin)
                    : false
                }
                onChange={() => {}}
              />
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                Event Name
              </span>
              <span className="text-[12px] font-[400] text-[#000]">
                {renderEventName(data)}
              </span>
            </div>

            {!isMobile && (
              <>
                <div className="opacity-0">field</div>

                <div className="opacity-0">field</div>
              </>
            )}
          </section>

          <section
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-4"
            }`}
          >
            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                Start Date Time
              </span>
              <span className="text-[12px] font-[400] text-[#000]">
                {dayjs(
                  data?.start_checkin_online_date,
                  "YYYY-MM-DD HH:mm:ss"
                ).format("DD/MM/YYYY HH:mm")}
              </span>
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                End Date Time
              </span>
              <span className="text-[12px] font-[400] text-[#000]">
                {dayjs(
                  data?.end_checkin_online_date,
                  "YYYY-MM-DD HH:mm:ss"
                ).format("DD/MM/YYYY HH:mm")}
              </span>
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                Authorization Form URL
              </span>

              {data?.authorization_form_url && (
                <a
                  href={data?.authorization_form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] font-[400] text-[#011BB6] underline"
                >
                  {data?.authorization_form_url}
                </a>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                Disclaimer Form URL
              </span>

              {data?.disclaimer_form_url && (
                <a
                  href={data?.disclaimer_form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] font-[400] text-[#011BB6] underline"
                >
                  {data?.disclaimer_form_url}
                </a>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                T&C Link
              </span>

              {data?.t_and_c_link && (
                <a
                  href={data?.t_and_c_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm w-fit text-[#011BB6] underline"
                >
                  {data?.t_and_c_link}
                </a>
              )}
            </div>
          </section>

          {/* eWaiver Banner */}
          <section className={`w-full ${isMobile ? "h-[137px]" : "h-[488px]"}`}>
            <img
              className="w-full h-full object-fit rounded-[8px]"
              src={
                data?.ewaiver_banner_url
                  ? data?.ewaiver_banner_url
                  : "../../images/background/empty-background.png"
              }
              alt="banner_url"
            />
          </section>

          <section
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-3"
            }`}
          >
            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                eWaiver File
              </span>

              {data?.url_html && data?.url_html !== "" && (
                <a
                  href={data?.url_html}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group hover:cursor-pointer"
                >
                  <div
                    className="w-[286px] px-[12px] py-[8px] rounded-[30px] bg-[#E9ECFF]
                                flex items-center gap-[8px]"
                  >
                    <PaperClipIcon className="size-[20px]" />

                    <label
                      className="w-[80%] max-w-[80%] truncate text-[12px] font-[500]
                                  group-hover:cursor-pointer group-hover:underline"
                    >
                      {data?.url_html}
                    </label>
                  </div>
                </a>
              )}

              {/* {data?.url_html && (
                <div
                  className="px-[12px] py-[8px] rounded-[30px] bg-[#E9ECFF]
                                                    flex items-center gap-[8px]"
                >
                  <PaperClipIcon className="size-[20px]" />

                  <label className="w-[144px] max-w-[144px] truncate text-[12px] font-[500]">
                    {data?.url_html?.name}
                  </label>
                </div>
              )} */}
            </div>

            {!isMobile && (
              <>
                <div className="opacity-0">field</div>

                <div className="opacity-0">field</div>
              </>
            )}
          </section>

          <section
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-3"
            }`}
          >
            <div className="flex items-center gap-[8px]">
              <Input
                type="checkbox"
                id="ewaiver_signature_required"
                checked={data?.waiver_sign_required}
                disable
              />

              <span className="text-[14px] font-[500] text-[#262626]">
                eWaiver Signature Required
              </span>
            </div>

            <div className="flex items-center gap-[8px]">
              <Input
                type="checkbox"
                id="send_email_to_authorizer"
                checked={data?.is_sent_to_authorizer}
                disable
              />

              <span className="text-[14px] font-[500] text-[#262626]">
                Send Email to Authorizer
              </span>
            </div>

            <div className="flex items-center gap-[8px]">
              <Input
                type="checkbox"
                id="send_bib_via_email"
                checked={data?.send_bib_via_email}
                disable
              />

              <span className="text-[14px] font-[500] text-[#262626]">
                Send BIB via Email
              </span>
            </div>
          </section>

          <section
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-3"
            }`}
          >
            <div className="flex items-center gap-[8px]">
              <Input
                type="checkbox"
                id="is_change_pickup_method"
                checked={data?.is_change_pickup_method}
                disable
              />

              <span className="text-[14px] font-[500] text-[#262626]">
                Change Pick-up Method
              </span>
            </div>

            <div className="flex items-center gap-[8px]">
              <Input
                type="checkbox"
                id="is_change_authorized_info"
                checked={data?.is_change_authorized_info}
                disable
              />

              <span className="text-[14px] font-[500] text-[#262626]">
                Change Authorized Info
              </span>
            </div>

            {!data?.waiver_sign_required ? (
              <div className="flex items-center gap-[8px]">
                <Input
                  type="checkbox"
                  id="only_authorize"
                  checked={data?.only_authorize}
                  disable
                />

                <span className="text-[14px] font-[500] text-[#262626]">
                  Authorize Only
                </span>
              </div>
            ) : (
              <div className={`opacity-0 ${isMobile && "hidden"}`}>field</div>
            )}
          </section>

          <section
            className={`w-full grid gap-5 ${
              isMobile ? "grid-cols-1" : "grid-cols-3"
            }`}
          >
            {data?.is_change_pickup_method ? (
              <div className="ml-[23px] flex flex-col gap-4">
                <span className="text-[14px] font-[500] text-[#262626]">
                  Max Number of Times to Update Pick-up Method
                </span>
                <span className="text-[12px] font-[400] text-[#000]">
                  {data?.number_of_times_change_pickup}
                </span>
              </div>
            ) : (
              <div className={`opacity-0 ${isMobile && "hidden"}`}>field</div>
            )}

            {data?.is_change_authorized_info ? (
              <div className="ml-[23px] flex flex-col gap-4">
                <span className="text-[14px] font-[500] text-[#262626]">
                  Max Number of Times to Update Authorized Info
                </span>
                <span className="text-[12px] font-[400] text-[#000]">
                  {data?.number_of_times_update_authorized_info}
                </span>
              </div>
            ) : (
              <div className={`opacity-0 ${isMobile && "hidden"}`}>field</div>
            )}

            <div className={`opacity-0 ${isMobile && "hidden"}`}>field</div>
          </section>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        label="Event Info In eWaiver Email"
        defaultOpen={true}
      >
        <div className="p-6 flex flex-col gap-[30px]">
          {data?.email_section &&
            data?.email_section?.length > 0 &&
            data?.email_section?.map((item) => {
              return (
                <div
                  key={generateUUID()}
                  className="bg-white rounded-lg border border-[#D9E1E9]"
                >
                  <CollapsibleSection
                    label={item?.title_en}
                    toolbar={
                      <div>
                        {item?.show ? (
                          <div
                            className="flex items-center justify-center bg-[#E9ECFF]
                                                                      rounded-[100px] px-[12px] py-[4px]"
                          >
                            <span className="text-[12px] font-[500] text-[#011BB6]">
                              Show
                            </span>
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center bg-[#E9EDF1]
                                                                      rounded-[100px] px-[12px] py-[4px]"
                          >
                            <span className="text-[12px] font-[500] text-[#868E95]">
                              Hide
                            </span>
                          </div>
                        )}
                      </div>
                    }
                  >
                    <div className="p-6 flex flex-col gap-[20px]">
                      <div
                        className={`w-full grid gap-4 ${
                          isMobile ? "grid-cols-1" : "grid-cols-2"
                        }`}
                      >
                        <div className="flex items-center gap-[8px]">
                          <img
                            src={`../../images/flags/circle-vi.png`}
                            alt="Language Icon"
                          />

                          <span className="text-[12px] font-[400] text-[#000] max-w-full truncate">
                            {item?.title_vi}
                          </span>
                        </div>

                        <div className="flex items-center gap-[8px]">
                          <img
                            src={`../../images/flags/circle-en.png`}
                            alt="Language Icon"
                          />

                          <span className="text-[12px] font-[400] text-[#000] max-w-full truncate">
                            {item?.title_en}
                          </span>
                        </div>
                      </div>

                      {item?.type === "url" && item?.value[0] && (
                        <>
                          {/* <div className="flex items-center gap-[37px]">
                            <RadioButton
                              disabled
                              checked
                              item={{
                                id: `${generateUUID()}`,
                                title: "URL",
                              }}
                              onChange={(e) => {}}
                            />

                            <RadioButton
                              disabled
                              checked={false}
                              item={{
                                id: `${generateUUID()}`,
                                title: "Image",
                              }}
                              onChange={(e) => {}}
                            />
                          </div> */}

                          <div className="flex flex-col gap-[8px]">
                            <span className="text-[14px] font-[500] text-[#262626]">
                              URL
                            </span>

                            <a
                              href={item?.value[0]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[12px] font-[400] text-[#011BB6] underline max-w-full truncate"
                            >
                              {item?.value[0]}
                            </a>
                          </div>
                        </>
                      )}

                      {item?.type === "image" && (
                        <>
                          <div className="flex flex-col gap-[8px]">
                            <span className="text-[14px] font-[500] text-[#262626]">
                              Image
                            </span>

                            <div className="flex items-center flex-wrap gap-[10px]">
                              {item?.value?.map((img: string) => {
                                return (
                                  <img
                                    key={generateUUID()}
                                    src={img}
                                    className="max-w-[200px] rounded-[8px]"
                                    alt="image-url"
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </CollapsibleSection>
                </div>
              );
            })}
        </div>
      </CollapsibleSection>

      <CollapsibleSection label="eKYC" defaultOpen={data?.kyc_required}>
        <div className="p-6 flex flex-col gap-[30px]">
          <section className="w-full grid grid-cols-3 gap-5">
            <div className="flex flex-col gap-4">
              <span className="text-[14px] font-[500] text-[#262626]">
                Use eKYC
              </span>

              <Input
                type="checkbox"
                id="kyc_required"
                checked={data?.kyc_required}
                disable
              />
            </div>
          </section>

          {data?.kyc_required && (
            <section className="w-full grid grid-cols-3 gap-5">
              <div className="flex flex-col gap-4">
                <span className="text-[14px] font-[500] text-[#262626]">
                  eKYC Orc Limit
                </span>

                <span className="text-[12px] font-[400] text-[#000]">
                  {data?.kyc_ocr_limit}
                </span>
              </div>

              <div className="flex flex-col gap-4">
                <span className="text-[14px] font-[500] text-[#262626]">
                  eKYC Face Matching Limit
                </span>

                <span className="text-[12px] font-[400] text-[#000]">
                  {data?.kyc_face_matching_limit}
                </span>
              </div>

              <div className="flex flex-col gap-4">
                <span className="text-[14px] font-[500] text-[#262626]">
                  eKYC Face Matching Ratio
                </span>

                <span className="text-[12px] font-[400] text-[#000]">
                  {data?.kyc_face_matching_ratio}
                </span>
              </div>
            </section>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default EwaiverDetailPage;
